import React, { createContext, useContext, useEffect, useState } from 'react';
import MessagesSocketService from '../services/sub/MessagesSocketService';
import NotificationSocketService from '../services/sub/NotificationSocketService';
import UniversalSocketService from '../services/sub/UniversalSocketService';
import userStatusSocketService from '../services/sub/UserStatusSocketService';
import CallSocketService from '../services/sub/CallSocketService';
import { CONTEXTS } from 'figshelf-modules';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const auth = CONTEXTS.useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [messageService] = useState(() => MessagesSocketService.getInstance());
  const [notificationService] = useState(() => NotificationSocketService.getInstance());
  const [universalService] = useState(() => UniversalSocketService.getInstance());

  useEffect(() => {
    let cancelled = false;

    const initializeSockets = async () => {
      if (!auth.isSignedIn || isInitialized || cancelled) return;

      try {
        await userStatusSocketService.initialize();
        await messageService.connect();
        await notificationService.connect();
        await universalService.connect();
        await CallSocketService.connect();

        if (cancelled) return;
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize socket services:', error);
      }
    };

    const disconnectSockets = async () => {
      try {
        await messageService.disconnect();
        await notificationService.disconnect();
        await universalService.disconnect();
        await userStatusSocketService.disconnect();
        await CallSocketService.disconnect();
        if (!cancelled) setIsInitialized(false);
      } catch (error) {
        console.error('Error during socket cleanup:', error);
      }
    };

    if (auth.isSignedIn) {
      initializeSockets();
    } else {
      disconnectSockets();
    }
    return () => {
      cancelled = true;
    };
  }, [auth.isSignedIn, isInitialized, messageService, notificationService, universalService]);

  useEffect(() => {
    return () => {
      const cleanup = async () => {
        try {
          await messageService.disconnect();
          await notificationService.disconnect();
          await universalService.disconnect();
          await userStatusSocketService.disconnect();
          await CallSocketService.disconnect();
        } catch (error) {
          console.error('Error during socket cleanup:', error);
        }
      };

      cleanup();
    };
  }, [messageService, notificationService, universalService]);

  const value = {
    messages: messageService,
    notifications: notificationService,
    universal: universalService,
    userStatus: userStatusSocketService,
    isInitialized,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};